<template>
  <div>
    <div style="display: flex; align-items: center; flex-direction: column; margin-bottom: 8px">
      <button class="btn btn-primary" style="margin-bottom: 10px" @click="apply" :disabled="applying">{{ $t('pos_price_sync.apply') }}</button>
      <div class="status-info" v-if="lastSyncAt">{{ $t('pos_price_sync.last_sync_at') }} {{lastSyncAt}}</div>
    </div>

    <table class="preview-table">
      <tr>
        <th style="width: 75px">{{ $t('type') }}</th>
        <th style="width: 200px">{{ $t('name') }}</th>
        <th style="width: 170px">{{ $t('pos_price_sync.pos_item') }}</th>
        <th style="width: 80px">{{ $t('method') }}</th>
        <th style="width: 60px">{{ $t('pos_price_sync.current_price') }}</th>
        <th style="width: 60px">{{ $t('pos_price_sync.pos_price') }}</th>
        <th style="width: 70px">{{ $t('pos_price_sync.new_price') }}</th>
        <th style="width: 60px">Δ{{ currencySymbol }}</th>
        <th style="width: 60px">Δ%</th>
        <th style="width: 60px">{{ $t('included') }}</th>
      </tr>

      <!-- Filters -->
      <tr class="filters">
        <td>
          <select v-model="localFilters['item.type']">
            <option :value="undefined"></option>
            <option v-for="iType of itemTypes" :value="iType">{{$t(`item_type.${iType}`)}}</option>
          </select>
        </td>
        <td><input type="text" v-model="localFilters['item.fullName']"></td>
        <td><input type="text" v-model="localFilters['posItem.fullName']"></td>
        <td>
          <select v-model="localFilters.method">
            <option :value="undefined"></option>
            <option v-for="m of methods" :value="m">{{$t(`deliveryMethod.${m}`)}}</option>
          </select>
        </td>
        <td><input type="text" v-model="localFilters.curPrice"></td>
        <td><input type="text" v-model="localFilters.posPrice"></td>
        <td><input type="text" v-model="localFilters.newPrice"></td>
        <td></td>
        <td></td>
        <td>
          <select v-model="localFilters.included">
            <option :value="undefined"></option>
            <option :value="true">{{$t('yes')}}</option>
            <option :value="false">{{$t('no')}}</option>
          </select>
        </td>
      </tr>

      <tr v-for="r of filteredRows" :key="`r_${r.item.id}_${r.method}`" :class="{ignored: !r.included}">
        <td>{{ $t(`item_type.${r.item.type}`) }}</td>
        <td>{{ $to(r.item.fullName) }}</td>
        <td>{{ r.posItem.fullName }}</td>
        <td>{{ $t(`deliveryMethod.${r.method}`) }}</td>
        <td>{{ formatMoney(r.curPrice) }}</td>
        <td>{{formatMoney(r.posPrice) }}</td>
        <td><input type="number" v-model="r.newPrice" style="width: 70px"></td>
        <td :class="deltaClasses(r.newPrice, r.curPrice)">{{ deltaMoney(r.newPrice, r.curPrice) }}</td>
        <td :class="deltaClasses(r.newPrice, r.curPrice)">{{ deltaPercent(r.newPrice, r.curPrice) }}</td>
        <td><input type="checkbox" v-model="r.included"></td>
      </tr>
    </table>
  </div>
</template>

<script>
import {Core} from "@/core/Core";
import {convertUTCToLocal, currencyToSymbol, debouncedComputedProperty, filterRows} from "@/vueUtils";

export default {
  name: "ExternalPosPriceSync",
  props: {
    inventory: Number,
    currency: String,
  },

  data() {
    return {
      rows: [],
      localFilters: {
        included: true
      },
      lastSyncAt: null,
      itemTypes: [
        'item',
        'variant',
        'modifier',
        'combo',
        'supplement',
      ],
      methods: [],
      applying: false,
    }
  },

  computed: {
    localFiltersDebounced: debouncedComputedProperty(555, {}),
    filteredRows() {
      return filterRows(this.rows, this.localFiltersDebounced);
    },
    currencySymbol() {
      return currencyToSymbol(this.currency);
    }
  },

  watch: {
    inventory: {
      immediate: true,
      async handler(i) {
        await this.load(i);
      }
    },
    localFilters: {
      immediate: true,
      deep: true,
      handler(val) {
        this.localFiltersDebounced = JSON.parse(JSON.stringify(val))
      }
    },
  },

  methods: {
    async load(inventory) {
      let response = await Core.noThrowRequest("GET", `/api/inventory/${inventory}/sync_preview`);
      if (!response) {
        return;
      }
      if (response.methods) {
        this.methods = response.methods;
      }
      if (response.statuses) {
        this.lastSyncAt = convertUTCToLocal(response.statuses.priceSyncedAt);
      }
      let rows = response.preview;
      for (let r of rows) {
        let posPrice = r.posPrice;
        let currentPrice = r.curPrice;
        let posPriceIsZero = this.priceEqual(0, posPrice);
        let currentPriceIsZero = this.priceEqual(0, currentPrice);
        let shouldSyncWithPos = !posPriceIsZero && !currentPriceIsZero;
        r.included = shouldSyncWithPos;
        r.newPrice = shouldSyncWithPos ? posPrice : currentPrice;
        r.posItem.fullName = `${r.posItem.id} - ${r.posItem.name}`;
      }

      rows.sort((r1, r2) => {
        if (r1.included !== r2.included) {
          return (r2.included ? 1 : 0) - (r1.included ? 1 : 0);
        } else {
          return Math.abs(r1.newPrice - r2.curPrice) - Math.abs(r2.newPrice - r1.curPrice);
        }
      });
      this.rows = rows;
    },

    async apply() {
      let inventory = this.inventory;
      let data = this.rows
        .filter(r => r.included)
        .map(({method, newPrice, item}) => ({method, newPrice, item: { id: item.id }}));

      this.applying = true;
      showSpinner();
      let response = await Core.noThrowRequest("POST", `/api/inventory/${inventory}/sync_apply`, data);
      hideSpinner();
      this.applying = false;

      if (response) {
        toast(`${response.changes.length} prices updated`, 'success');

        let changesMap = {};
        for (let change of response.changes) {
          let itemID = change.item.id;
          let method = change.method;
          changesMap[`${itemID}_${method}`] = parseFloat(change.n);
        }
        for (let row of this.rows) {
          let k = `${row.item.id}_${row.method}`;
          let v = changesMap[k];
          if (typeof v === 'number' && !isNaN(v)) {
            row.curPrice = v;
            row.newPrice = v;
          }
        }
      }
    },

    deltaMoney(n, o) {
      if (this.priceEqual(n, o)) {
        return '';
      }
      let diff = n - o;
      let diffAsMoney = this.formatMoney(diff);
      return `${diffAsMoney}`;
    },

    deltaPercent(n, o) {
      if (this.priceEqual(n, o)) {
        return '';
      }
      let diff = n - o;
      let diffAsPercent = diff/o*100;
      return `${Math.round(diffAsPercent * 10) / 10}%`;
    },

    deltaClasses(n, o) {
      let diff = n - o;
      if (this.priceEqual(diff, 0)) {
        return [];
      } else if (diff > 0) {
        return ['positive'];
      } else {
        return ['negative'];
      }
    },

    formatMoney(m) {
      return m.toFixed(2);
    },

    priceEqual(p1, p2) {
      return Math.abs(p1 - p2) < 0.001;
    }
  }
}
</script>

<style lang="scss" scoped>
.status-info {
  font-size: smaller;
}

.preview-table {
  width: 100%;
  table-layout: fixed;

  td,
  th {
    padding: 2px 2px;
    text-align: center;
  }

  tr:nth-child(odd) {
    background-color: #eee;
  }

  tr+.ignored {
    background-color: rgba(144, 12, 63, 0.25);
  }

  tr+.modified {
    background-color: lightseagreen;
  }

  th {
    white-space: nowrap;
    background-color: #ddd;
  }

  button {
    padding: 3px 8px;
  }

  input[type=number] {
    padding: 2px 2px;
  }

  input[type=checkbox] {
    display: inline;
    transform: scale(1.5);
  }

  .positive {
    color: cornflowerblue;
  }

  .negative {
    color: indianred;
  }

  .filters {
    input, select {
      padding: 0;
      height: 24px;
      width: 100%;
    }
  }
}
</style>